import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, { getErrorMessage } from '../../utils/api';

const ADMIN = '/admin';

function isPlainObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function extractListPayload(payload, preferredKeys = []) {
  if (Array.isArray(payload)) return payload;
  if (!isPlainObject(payload)) return [];

  const keysToCheck = [...preferredKeys, 'items', 'data', 'results'];
  for (const key of keysToCheck) {
    const value = payload[key];
    if (Array.isArray(value)) return value;
    if (isPlainObject(value)) {
      const nested = extractListPayload(value, preferredKeys);
      if (nested.length > 0) return nested;
    }
  }

  return [];
}

function extractPaginationPayload(payload) {
  if (!isPlainObject(payload)) {
    return { page: 1, limit: 20, total: 0, totalPages: 0 };
  }

  const source = payload.pagination ?? payload.meta ?? payload;
  const page = Number(source.page ?? source.current_page ?? 1);
  const limit = Number(source.limit ?? source.per_page ?? source.page_size ?? 20);
  const total = Number(source.total ?? source.total_count ?? source.totalCount ?? 0);
  const totalPages = Number(
    source.totalPages ??
      source.total_pages ??
      (limit > 0 ? Math.ceil(total / limit) : 0)
  );

  return {
    page: Number.isFinite(page) ? page : 1,
    limit: Number.isFinite(limit) ? limit : 20,
    total: Number.isFinite(total) ? total : 0,
    totalPages: Number.isFinite(totalPages) ? totalPages : 0,
  };
}

// GET /admin/withdrawal-requests/:id
export const fetchWithdrawRequestById = createAsyncThunk(
  'withdrawRequest/fetchWithdrawRequestById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${ADMIN}/withdrawal-requests/${id}`);
      return data?.data ?? data ?? null;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch withdraw request');
    }
  }
);

// GET /admin/withdrawal-requests (query: page, limit, status)
export const fetchWithdrawRequests = createAsyncThunk(
  'withdrawRequest/fetchWithdrawRequests',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (filters.page) params.page = filters.page;
      if (filters.limit) params.limit = filters.limit;
      if (filters.status && filters.status !== 'all') params.status = filters.status;

      const { data } = await api.get(`${ADMIN}/withdrawal-requests`, { params });
      const payload = data?.data ?? data;

      return {
        withdrawRequests: extractListPayload(payload, ['items']),
        pagination: extractPaginationPayload(payload),
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch withdraw requests');
    }
  }
);

// PATCH /admin/withdrawal-requests/:id/status (body: status, admin_note)
export const updateWithdrawRequestStatus = createAsyncThunk(
  'withdrawRequest/updateWithdrawRequestStatus',
  async ({ id, status, admin_note }, { rejectWithValue }) => {
    try {
      const body = { status };
      if (admin_note) body.admin_note = admin_note;

      const { data } = await api.patch(`${ADMIN}/withdrawal-requests/${id}/status`, body);
      return data?.data ?? data ?? {};
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to update withdraw request status');
    }
  }
);

const initialState = {
  withdrawRequests: [],
  selectedWithdrawRequest: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
  loading: false,
  detailLoading: false,
  error: null,
};

const withdrawRequestSlice = createSlice({
  name: 'withdrawRequest',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedWithdrawRequest: (state) => {
      state.selectedWithdrawRequest = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWithdrawRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWithdrawRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.withdrawRequests = action.payload?.withdrawRequests ?? [];
        state.pagination = action.payload?.pagination ?? state.pagination;
      })
      .addCase(fetchWithdrawRequests.rejected, (state, action) => {
        state.loading = false;
        state.withdrawRequests = [];
        state.error = action.payload || 'Failed to fetch withdraw requests';
      })
      .addCase(fetchWithdrawRequestById.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchWithdrawRequestById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedWithdrawRequest = action.payload;
      })
      .addCase(fetchWithdrawRequestById.rejected, (state, action) => {
        state.detailLoading = false;
        state.selectedWithdrawRequest = null;
        state.error = action.payload || 'Failed to fetch withdraw request';
      })
      .addCase(updateWithdrawRequestStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWithdrawRequestStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.withdrawRequests.findIndex((r) => r.id === action.payload?.id);
        if (index !== -1) state.withdrawRequests[index] = { ...state.withdrawRequests[index], ...action.payload };
        if (state.selectedWithdrawRequest?.id === action.payload?.id) {
          state.selectedWithdrawRequest = { ...state.selectedWithdrawRequest, ...action.payload };
        }
      })
      .addCase(updateWithdrawRequestStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update withdraw request status';
      });
  },
});

export const { clearError, clearSelectedWithdrawRequest } = withdrawRequestSlice.actions;
export default withdrawRequestSlice.reducer;

export const selectWithdrawRequests = (state) => state.withdrawRequest.withdrawRequests;
export const selectSelectedWithdrawRequest = (state) => state.withdrawRequest.selectedWithdrawRequest;
export const selectWithdrawRequestPagination = (state) => state.withdrawRequest.pagination;
export const selectWithdrawRequestLoading = (state) => state.withdrawRequest.loading;
export const selectWithdrawRequestDetailLoading = (state) => state.withdrawRequest.detailLoading;
export const selectWithdrawRequestError = (state) => state.withdrawRequest.error;

export function useWithdrawRequest() {
  const dispatch = useDispatch();
  const withdrawRequests = useSelector(selectWithdrawRequests);
  const selectedWithdrawRequest = useSelector(selectSelectedWithdrawRequest);
  const pagination = useSelector(selectWithdrawRequestPagination);
  const loading = useSelector(selectWithdrawRequestLoading);
  const detailLoading = useSelector(selectWithdrawRequestDetailLoading);
  const error = useSelector(selectWithdrawRequestError);

  return {
    withdrawRequests,
    selectedWithdrawRequest,
    pagination,
    loading,
    detailLoading,
    error,
    fetchWithdrawRequests: useCallback((filters) => dispatch(fetchWithdrawRequests(filters)), [dispatch]),
    fetchWithdrawRequestById: useCallback((id) => dispatch(fetchWithdrawRequestById(id)), [dispatch]),
    updateWithdrawRequestStatus: useCallback(
      ({ id, status, admin_note }) => dispatch(updateWithdrawRequestStatus({ id, status, admin_note })),
      [dispatch]
    ),
    clearSelectedWithdrawRequest: useCallback(() => dispatch(clearSelectedWithdrawRequest()), [dispatch]),
  };
}
